<template>
  <div class="kanban-board">
    <div class="board-layout">
      <!-- Competency Groups Section -->
      <div v-if="!isSummary" class="competency-groups-section">
        <div class="filter-checkboxes">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="showTechnical"
              @change="updateFilteredCompetencies"
              title="Show/hide Technical competencies"
            />
            <span>Technical</span>
          </label>
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="showNonTechnical"
              @change="updateFilteredCompetencies"
              title="Show/hide Non-Technical competencies"
            />
            <span>Non-Tech</span>
          </label>
        </div>
        <div class="scroll-competency-section">
          <div class="competency-groups">
            <div
              v-for="group in displayCompetencyGroups"
              :key="group.competencyGrouping"
              class="competency-group"
            >
              <div class="group-header-container">
                <div class="tooltip-container">
                  <button
                    class="info-button"
                    @mouseenter="
                      showTooltip(
                        $event,
                        group.competencyGrouping,
                        group.competencyGroupingDescription
                      )
                    "
                    @mouseleave="hideTooltip"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </button>
                  <div class="type-indicators">
                    <span
                      v-if="
                        group.competencies.some((c) => c.type === 'Technical')
                      "
                      class="type-dot technical"
                      title="Contains Technical items"
                    ></span>
                    <span
                      v-if="
                        group.competencies.some(
                          (c) => c.type === 'Non Technical'
                        )
                      "
                      class="type-dot non-technical"
                      title="Contains Non-Technical items"
                    ></span>
                  </div>
                </div>
                <div
                  class="group-header"
                  :class="{
                    'group-disabled': hasPartialItems(group.competencyGrouping),
                  }"
                  :draggable="!hasPartialItems(group.competencyGrouping)"
                  @dragstart="startDragGroup($event, group)"
                  @dragend="handleDragEnd"
                  @click="toggleGroup(group.competencyGrouping)"
                  :title="
                    hasPartialItems(group.competencyGrouping)
                      ? 'Cannot drag group - some items are already in proficiency levels'
                      : 'Drag to move entire group'
                  "
                >
                  <div class="group-title-container">
                    <span class="drag-handle">⋮⋮</span>
                    <span class="group-title">{{
                      group.competencyGrouping
                    }}</span>
                  </div>
                  <span class="expand-icon">
                    {{
                      expandedGroups[String(group.competencyGrouping)]
                        ? "−"
                        : "+"
                    }}
                  </span>
                </div>
              </div>
              <div
                v-show="expandedGroups[String(group.competencyGrouping)]"
                class="group-competencies"
              >
                <div
                  v-for="competency in group.competencies"
                  :key="competency.competencyTitle"
                  class="competency-item"
                  :data-type="competency.type"
                  draggable="true"
                  @dragstart="
                    startDragCompetency(
                      $event,
                      competency,
                      group.competencyGrouping
                    )
                  "
                  @dragend="handleDragEnd"
                >
                  <div class="competency-content">
                    <div class="tooltip-container">
                      <button
                        class="info-button"
                        @mouseenter="
                          showTooltip(
                            $event,
                            competency.competencyTitle,
                            competency.competencyTitleDescription
                          )
                        "
                        @mouseleave="hideTooltip"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                      </button>
                    </div>
                    <span class="competency-title">{{
                      competency.competencyTitle
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Proficiencies Section -->
      <div class="proficiencies-section" :class="{ 'summary-mode': isSummary }">
        <div class="proficiencies-grid" :class="{ 'summary-mode': isSummary }">
          <div
            v-for="proficiency in proficiencies"
            :key="proficiency.proficiencyTitle"
            v-show="
              !isSummary ||
              droppedCompetencies[proficiency.proficiencyTitle]?.length > 0
            "
            class="proficiency-box"
            :class="{ 'summary-mode': isSummary }"
            @dragover.prevent
            @drop="onDrop($event, proficiency.proficiencyTitle)"
          >
            <div class="proficiency-header">
              <h3 class="font-semibold">
                {{ proficiency.proficiencyTitle }}
              </h3>
              <div
                class="tooltip-container"
                @mouseenter="
                  showTooltip(
                    $event,
                    proficiency.proficiencyTitle,
                    proficiency.proficiencyDescription
                  )
                "
                @mouseleave="hideTooltip"
              >
                <button class="info-button">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div class="dropped-competencies">
              <div class="competencies-list">
                <!-- Grouped competencies display -->
                <div
                  v-for="(groupData, groupName) in groupedDroppedCompetencies[
                    proficiency.proficiencyTitle
                  ]"
                  :key="groupName"
                  class="dropped-group"
                  draggable="true"
                  @dragstart="
                    startDragDroppedGroup(
                      $event,
                      groupData,
                      groupName,
                      proficiency.proficiencyTitle
                    )
                  "
                  @dragend="handleDragEnd"
                >
                  <div class="group-header-dropped">
                    <div class="group-title-container-dropped">
                      <span class="drag-handle-dropped">⋮⋮</span>
                      <span class="group-title-dropped">{{ groupName }}</span>
                    </div>
                    <button
                      class="remove-group-button"
                      @click="
                        removeGroupFromProficiency(
                          groupName,
                          proficiency.proficiencyTitle
                        )
                      "
                      title="Remove entire group from proficiency level"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                  <div class="group-competencies-dropped">
                    <div
                      v-for="(competency, index) in groupData"
                      :key="index"
                      class="dropped-item"
                      :data-type="competency.type"
                    >
                      {{ competency.competencyTitle }}
                      <button
                        class="remove-button"
                        @click="
                          removeFromProficiency(
                            competency,
                            proficiency.proficiencyTitle
                          )
                        "
                        title="Remove from proficiency level"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Portal for tooltip -->
  <Teleport to="body">
    <div
      v-if="activeTooltip"
      class="global-tooltip"
      :style="{
        top: `${activeTooltip.y}px`,
        left: `${activeTooltip.x}px`,
      }"
    >
      <div class="tooltip-title">{{ activeTooltip.title }}</div>
      <div class="tooltip-description">{{ activeTooltip.description }}</div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, nextTick, watch } from "vue";
import {
  competencyGroups as originalCompetencyGroups,
  proficiencies,
} from "../stores/constants";

// Create a deep copy of the original competency groups to prevent mutations
const competencyGroups: typeof originalCompetencyGroups = JSON.parse(
  JSON.stringify(originalCompetencyGroups)
);
import {
  droppedCompetencies,
  setDroppedCompetencies,
} from "../stores/gameStore";

interface DroppedCompetency {
  competencyTitle: string;
  competencyGrouping: string;
  type?: string;
}

interface Competency {
  competencyTitle: string;
  competencyTitleDescription: string;
  type?: string;
}

interface CompetencyGroup {
  competencyGrouping: string;
  competencyGroupingDescription: string;
  competencies: Competency[];
}

const props = defineProps<{
  filteredCompetencyGroups?: typeof competencyGroups;
  isSummary?: boolean;
}>();

const isSummary = ref(props.isSummary || false);
const expandedGroups = ref<{ [key: string]: boolean }>({});
const localCompetencyGroups = ref([...competencyGroups]);
const showTechnical = ref(true);
const showNonTechnical = ref(true);
const duplicationCheckInterval = ref<number | null>(null);

// Function to check for and remove duplications
const checkForDuplications = () => {
  // Get all competencies currently on the board
  const boardCompetencies = new Set<string>();
  Object.values(droppedCompetencies.value).forEach((competencies) => {
    competencies.forEach((competency) => {
      const key = `${competency.competencyGrouping}:${competency.competencyTitle}`;
      boardCompetencies.add(key);
    });
  });

  // Check each local competency group and remove any items that are on the board
  localCompetencyGroups.value.forEach((group) => {
    const originalLength = group.competencies.length;
    group.competencies = group.competencies.filter((competency) => {
      const key = `${group.competencyGrouping}:${competency.competencyTitle}`;
      const isOnBoard = boardCompetencies.has(key);

      if (isOnBoard) {
        console.log(
          `Removing duplicate: ${competency.competencyTitle} from ${group.competencyGrouping}`
        );
      }

      return !isOnBoard;
    });

    if (group.competencies.length !== originalLength) {
      console.log(
        `Removed ${
          originalLength - group.competencies.length
        } duplicates from ${group.competencyGrouping}`
      );
    }
  });

  // Also check for duplicates within the board itself (same item in multiple proficiency levels)
  const seenCompetencies = new Map<string, string>(); // key -> proficiency level
  const duplicatesToRemove: {
    proficiency: string;
    competency: DroppedCompetency;
  }[] = [];

  Object.entries(droppedCompetencies.value).forEach(
    ([proficiency, competencies]) => {
      competencies.forEach((competency) => {
        const key = `${competency.competencyGrouping}:${competency.competencyTitle}`;

        if (seenCompetencies.has(key)) {
          const existingProficiency = seenCompetencies.get(key)!;
          console.log(
            `Found duplicate on board: ${competency.competencyTitle} in both ${existingProficiency} and ${proficiency}`
          );
          duplicatesToRemove.push({ proficiency, competency });
        } else {
          seenCompetencies.set(key, proficiency);
        }
      });
    }
  );

  // Remove duplicates from the board
  duplicatesToRemove.forEach(({ proficiency, competency }) => {
    droppedCompetencies.value[proficiency] = droppedCompetencies.value[
      proficiency
    ].filter(
      (c) =>
        !(
          c.competencyTitle === competency.competencyTitle &&
          c.competencyGrouping === competency.competencyGrouping
        )
    );
  });

  if (duplicatesToRemove.length > 0) {
    console.log(
      `Removed ${duplicatesToRemove.length} duplicates from the board`
    );
    setDroppedCompetencies(droppedCompetencies.value);
  }
};

// Start periodic duplication checks
const startDuplicationChecks = () => {
  if (duplicationCheckInterval.value) {
    clearInterval(duplicationCheckInterval.value);
  }

  // Run check every 3 seconds
  duplicationCheckInterval.value = setInterval(checkForDuplications, 3000);

  // Also run immediately
  checkForDuplications();
};

// Stop periodic duplication checks
const stopDuplicationChecks = () => {
  if (duplicationCheckInterval.value) {
    clearInterval(duplicationCheckInterval.value);
    duplicationCheckInterval.value = null;
  }
};

// Watch for changes in dropped competencies and run duplication check
watch(
  () => droppedCompetencies.value,
  () => {
    // Run duplication check after any change to dropped competencies
    nextTick(() => {
      checkForDuplications();
    });
  },
  { deep: true }
);

// Lifecycle hooks
onMounted(() => {
  console.log("KanbanBoard mounted - starting duplication checks");
  startDuplicationChecks();
});

onUnmounted(() => {
  console.log("KanbanBoard unmounted - stopping duplication checks");
  stopDuplicationChecks();
});

// Use filtered competencies if provided, otherwise use local competencies
const displayCompetencyGroups = computed(() => {
  if (props.filteredCompetencyGroups) {
    return props.filteredCompetencyGroups;
  }

  // Always show all groups, even if they're empty, so items can be restored
  return localCompetencyGroups.value.map((group) => ({
    ...group,
    competencies: group.competencies
      .filter((competency) => {
        if (showTechnical.value && showNonTechnical.value) return true;
        if (showTechnical.value && competency.type === "Technical") return true;
        if (showNonTechnical.value && competency.type === "Non Technical")
          return true;
        return false;
      })
      .map((competency) => ({
        ...competency,
        type: competency.type || "Non Technical", // Ensure type is always defined
      })),
  }));
});

// Group dropped competencies by their grouping for display
const groupedDroppedCompetencies = computed(() => {
  const grouped: { [key: string]: { [key: string]: DroppedCompetency[] } } = {};

  Object.entries(droppedCompetencies.value).forEach(
    ([proficiency, competencies]) => {
      grouped[proficiency] = {};

      competencies.forEach((competency) => {
        if (!grouped[proficiency][competency.competencyGrouping]) {
          grouped[proficiency][competency.competencyGrouping] = [];
        }
        grouped[proficiency][competency.competencyGrouping].push(competency);
      });
    }
  );

  return grouped;
});

const updateFilteredCompetencies = (event: Event) => {
  // If both checkboxes are unchecked, prevent unchecking
  if (!showTechnical.value && !showNonTechnical.value) {
    if (
      event.target ===
      document.querySelector('input[type="checkbox"]:first-child')
    ) {
      showNonTechnical.value = true;
    } else {
      showTechnical.value = true;
    }
  }
};

// Toggle group expansion
const toggleGroup = (groupName: string | number) => {
  expandedGroups.value[String(groupName)] =
    !expandedGroups.value[String(groupName)];
};

// Check if a group has partial items (some items are in proficiency levels)
const hasPartialItems = (groupName: string) => {
  // Check if any competencies from this group are in any proficiency level
  return Object.values(droppedCompetencies.value).some((competencies) =>
    competencies.some((c) => c.competencyGrouping === groupName)
  );
};

// Start dragging an entire group
const startDragGroup = (event: DragEvent, group: CompetencyGroup) => {
  // Stop event propagation to prevent conflicts with individual item dragging
  event.stopPropagation();

  // Check if this group has partial items - if so, disable group dragging
  if (hasPartialItems(group.competencyGrouping)) {
    console.log(
      "Cannot drag group - it has partial items in proficiency levels"
    );
    event.preventDefault();
    return;
  }

  if (event.dataTransfer) {
    event.dataTransfer.setData(
      "competencyGroup",
      JSON.stringify({
        competencyGrouping: group.competencyGrouping,
        competencies: group.competencies.map((comp) => ({
          competencyTitle: comp.competencyTitle,
          competencyGrouping: group.competencyGrouping,
          type: comp.type || "Non Technical",
        })),
      })
    );
    const target = event.target as HTMLElement;
    target.classList.add("dragging");
  }
};

// Start dragging an individual competency
const startDragCompetency = (
  event: DragEvent,
  competency: Competency,
  competencyGrouping: string
) => {
  // Stop event propagation to prevent the group drag from triggering
  event.stopPropagation();

  if (event.dataTransfer) {
    event.dataTransfer.setData(
      "competency",
      JSON.stringify({
        competencyTitle: competency.competencyTitle,
        competencyGrouping: competencyGrouping,
        type: competency.type || "Non Technical",
      })
    );
    const target = event.target as HTMLElement;
    target.classList.add("dragging");
  }
};

// Start dragging a group from a proficiency level
const startDragDroppedGroup = (
  event: DragEvent,
  groupData: DroppedCompetency[],
  groupName: string | number,
  sourceProficiency: string
) => {
  // Stop event propagation to prevent conflicts
  event.stopPropagation();

  if (event.dataTransfer) {
    event.dataTransfer.setData(
      "droppedGroup",
      JSON.stringify({
        groupName: String(groupName),
        groupData,
        sourceProficiency,
      })
    );
    const target = event.target as HTMLElement;
    target.classList.add("dragging");
  }
};

const handleDragEnd = (event: DragEvent) => {
  // Stop event propagation to prevent the group drag end from triggering
  event.stopPropagation();

  const target = event.target as HTMLElement;
  target.style.transform = "scale(1)";
  target.style.boxShadow = "none";
  target.classList.remove("dragging");
};

const onDrop = (event: DragEvent, proficiencyTitle: string) => {
  if (event.dataTransfer) {
    // Check if it's a dropped group from another proficiency level
    const droppedGroupData = event.dataTransfer.getData("droppedGroup");
    if (droppedGroupData) {
      const droppedGroup = JSON.parse(droppedGroupData);

      // Remove the group from the source proficiency level
      droppedCompetencies.value[droppedGroup.sourceProficiency] =
        droppedCompetencies.value[droppedGroup.sourceProficiency].filter(
          (c) => c.competencyGrouping !== droppedGroup.groupName
        );

      // Add the group to the top of the new proficiency level
      if (!droppedCompetencies.value[proficiencyTitle]) {
        droppedCompetencies.value[proficiencyTitle] = [];
      }
      droppedCompetencies.value[proficiencyTitle].unshift(
        ...droppedGroup.groupData
      );
      setDroppedCompetencies(droppedCompetencies.value);

      // Run immediate duplication check
      nextTick(() => {
        checkForDuplications();
      });
      return;
    }

    // Check if it's a group drop from original competency groups
    const groupData = event.dataTransfer.getData("competencyGroup");
    if (groupData) {
      const group = JSON.parse(groupData);

      if (!droppedCompetencies.value[proficiencyTitle]) {
        droppedCompetencies.value[proficiencyTitle] = [];
      }

      // Enhanced duplication prevention: Remove from ALL proficiency levels first
      Object.keys(droppedCompetencies.value).forEach((level) => {
        droppedCompetencies.value[level] = droppedCompetencies.value[
          level
        ].filter((c) => c.competencyGrouping !== group.competencyGrouping);
      });

      // Remove from original competency groups
      const groupIndex = localCompetencyGroups.value.findIndex(
        (g) => g.competencyGrouping === group.competencyGrouping
      );
      if (groupIndex !== -1) {
        localCompetencyGroups.value[groupIndex].competencies = [];
      }

      // Add all competencies from the group to the top of the new proficiency level
      droppedCompetencies.value[proficiencyTitle].unshift(
        ...group.competencies
      );
      setDroppedCompetencies(droppedCompetencies.value);

      // Run immediate duplication check
      nextTick(() => {
        checkForDuplications();
      });
      return;
    }

    // Fallback for individual competency drops (legacy support)
    const data = event.dataTransfer.getData("competency");
    if (data) {
      const competency = JSON.parse(data) as DroppedCompetency;

      if (!droppedCompetencies.value[proficiencyTitle]) {
        droppedCompetencies.value[proficiencyTitle] = [];
      }

      // Enhanced duplication prevention: Remove from ALL proficiency levels first
      Object.keys(droppedCompetencies.value).forEach((level) => {
        droppedCompetencies.value[level] = droppedCompetencies.value[
          level
        ].filter(
          (c) =>
            !(
              c.competencyTitle === competency.competencyTitle &&
              c.competencyGrouping === competency.competencyGrouping
            )
        );
      });

      // Remove from original competency groups
      const groupIndex = localCompetencyGroups.value.findIndex(
        (g) => g.competencyGrouping === competency.competencyGrouping
      );
      if (groupIndex !== -1) {
        localCompetencyGroups.value[groupIndex].competencies =
          localCompetencyGroups.value[groupIndex].competencies.filter(
            (c) => c.competencyTitle !== competency.competencyTitle
          );
      }

      // Add the competency to the top of the new proficiency level
      droppedCompetencies.value[proficiencyTitle].unshift(competency);
      setDroppedCompetencies(droppedCompetencies.value);

      // Run immediate duplication check
      nextTick(() => {
        checkForDuplications();
      });
    }
  }
};

const removeFromProficiency = (
  competency: DroppedCompetency,
  proficiencyTitle: string
) => {
  console.log("Removing competency:", competency);
  console.log("From proficiency:", proficiencyTitle);

  // Remove from proficiency level
  droppedCompetencies.value[proficiencyTitle] = droppedCompetencies.value[
    proficiencyTitle
  ].filter(
    (c) =>
      !(
        c.competencyTitle === competency.competencyTitle &&
        c.competencyGrouping === competency.competencyGrouping
      )
  );
  setDroppedCompetencies(droppedCompetencies.value);

  // Run immediate duplication check after removal
  nextTick(() => {
    checkForDuplications();
  });

  // Find the original competency to get its full details
  const originalGroup = competencyGroups.find(
    (g) => g.competencyGrouping === competency.competencyGrouping
  );

  console.log("Original group found:", !!originalGroup);
  console.log("Looking for competency:", competency.competencyTitle);
  console.log("In group:", competency.competencyGrouping);

  if (originalGroup) {
    console.log(
      "Available competencies in original group:",
      originalGroup.competencies.map((c) => c.competencyTitle)
    );
  }

  const originalCompetency = originalGroup?.competencies.find(
    (c) => c.competencyTitle === competency.competencyTitle
  );

  console.log("Original competency found:", !!originalCompetency);

  if (originalCompetency && originalGroup) {
    // Find or create the group in localCompetencyGroups
    let groupIndex = localCompetencyGroups.value.findIndex(
      (g) => g.competencyGrouping === competency.competencyGrouping
    );

    console.log("Group index in local groups:", groupIndex);

    // If group doesn't exist in local groups, create it with all original competencies
    if (groupIndex === -1) {
      console.log(
        "Creating new group in local groups with all original competencies"
      );
      const newGroup = {
        competencyGrouping: competency.competencyGrouping,
        competencyGroupingDescription:
          originalGroup.competencyGroupingDescription,
        competencies: [...originalGroup.competencies], // Copy all original competencies
      };
      localCompetencyGroups.value.push(newGroup);
      groupIndex = localCompetencyGroups.value.length - 1;
    } else {
      // If group exists, ensure the competency is not already there
      console.log("Group exists, checking if competency is already present");
    }

    // Check if competency is not already in the group
    const competencyExists = localCompetencyGroups.value[
      groupIndex
    ].competencies.some(
      (c) => c.competencyTitle === competency.competencyTitle
    );

    console.log("Competency already exists in group:", competencyExists);

    if (!competencyExists) {
      console.log("Adding competency back to group");
      const newCompetency = {
        competencyTitle: originalCompetency.competencyTitle,
        competencyTitleDescription:
          originalCompetency.competencyTitleDescription,
        type: originalCompetency.type || "Non Technical",
      };
      localCompetencyGroups.value[groupIndex].competencies.push(newCompetency);
      console.log("Competency added successfully");
    }
  } else {
    console.log(
      "Could not find original group or competency, creating basic competency"
    );

    // Fallback: Create the competency with basic information
    let groupIndex = localCompetencyGroups.value.findIndex(
      (g) => g.competencyGrouping === competency.competencyGrouping
    );

    if (groupIndex === -1) {
      const newGroup = {
        competencyGrouping: competency.competencyGrouping,
        competencyGroupingDescription: "Restored group",
        competencies: [],
      };
      localCompetencyGroups.value.push(newGroup);
      groupIndex = localCompetencyGroups.value.length - 1;
    }

    const competencyExists = localCompetencyGroups.value[
      groupIndex
    ].competencies.some(
      (c) => c.competencyTitle === competency.competencyTitle
    );

    if (!competencyExists) {
      const newCompetency = {
        competencyTitle: competency.competencyTitle,
        competencyTitleDescription: "Restored competency",
        type: competency.type || "Non Technical",
      };
      localCompetencyGroups.value[groupIndex].competencies.push(newCompetency);
      console.log("Competency restored with basic information");
    }
  }
};

const removeGroupFromProficiency = async (
  groupName: string | number,
  proficiencyTitle: string
) => {
  console.log("removeGroupFromProficiency called with:", {
    groupName,
    proficiencyTitle,
  });

  const groupNameStr = String(groupName);
  console.log("Group name string:", groupNameStr);

  // Remove all competencies from this group from the proficiency level
  const beforeCount = droppedCompetencies.value[proficiencyTitle]?.length || 0;
  droppedCompetencies.value[proficiencyTitle] = droppedCompetencies.value[
    proficiencyTitle
  ].filter((c) => c.competencyGrouping !== groupNameStr);
  const afterCount = droppedCompetencies.value[proficiencyTitle]?.length || 0;
  console.log(
    `Removed competencies from ${proficiencyTitle}: ${beforeCount} -> ${afterCount}`
  );

  setDroppedCompetencies(droppedCompetencies.value);

  // Run immediate duplication check after removal
  nextTick(() => {
    checkForDuplications();
  });

  // Find the original group to get all competencies
  // Use the original competencyGroups constant, not the localCompetencyGroups
  const originalGroup = originalCompetencyGroups.find(
    (g) => g.competencyGrouping === groupNameStr
  );
  console.log("Original group found:", !!originalGroup);
  if (originalGroup) {
    console.log(
      "Original group competencies count:",
      originalGroup.competencies.length
    );
  }

  if (originalGroup) {
    // Find or create the group in localCompetencyGroups
    let groupIndex = localCompetencyGroups.value.findIndex(
      (g) => g.competencyGrouping === groupNameStr
    );
    console.log("Group index in local groups:", groupIndex);

    // If group doesn't exist in local groups, create it
    if (groupIndex === -1) {
      console.log("Creating new group in local groups");
      const newGroup = {
        competencyGrouping: groupNameStr,
        competencyGroupingDescription:
          originalGroup.competencyGroupingDescription,
        competencies: [],
      };
      localCompetencyGroups.value.push(newGroup);
      groupIndex = localCompetencyGroups.value.length - 1;
      console.log("New group created at index:", groupIndex);
    }

    // Restore ALL competencies from the original group back to the local group
    // This ensures the group is completely restored regardless of current state
    const beforeRestoreCount =
      localCompetencyGroups.value[groupIndex].competencies.length;

    // Force reactivity by creating a new array
    const restoredCompetencies = originalGroup.competencies.map(
      (originalCompetency) => ({
        competencyTitle: originalCompetency.competencyTitle,
        competencyTitleDescription:
          originalCompetency.competencyTitleDescription,
        type: originalCompetency.type || "Non Technical",
      })
    );

    localCompetencyGroups.value[groupIndex].competencies = restoredCompetencies;

    // Force reactivity update
    localCompetencyGroups.value = [...localCompetencyGroups.value];

    const afterRestoreCount =
      localCompetencyGroups.value[groupIndex].competencies.length;
    console.log(
      `Restored competencies to local group: ${beforeRestoreCount} -> ${afterRestoreCount}`
    );
    console.log(
      "Restored competencies:",
      localCompetencyGroups.value[groupIndex].competencies.map(
        (c) => c.competencyTitle
      )
    );

    // Wait for next tick to ensure DOM updates
    await nextTick();
    console.log("DOM should be updated now");
  } else {
    console.log("No original group found for:", groupNameStr);
  }
};

// Tooltip state
const activeTooltip = ref<{
  title: string;
  description: string;
  x: number;
  y: number;
} | null>(null);

// Position tooltip relative to the info button
const showTooltip = (
  event: MouseEvent,
  title: string | number,
  description: string
) => {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const tooltipWidth = 300; // Width of the tooltip
  const tooltipHeight = 100; // Approximate height of the tooltip
  const padding = 10; // Padding from viewport edges

  // Calculate initial position
  let x = rect.left;
  let y = rect.bottom + window.scrollY + 10;

  // Check if tooltip would go off the right edge
  if (x + tooltipWidth + padding > window.innerWidth) {
    x = window.innerWidth - tooltipWidth - padding;
  }

  // Check if tooltip would go off the bottom edge
  if (y + tooltipHeight + padding > window.innerHeight + window.scrollY) {
    y = rect.top + window.scrollY - tooltipHeight - padding;
  }

  activeTooltip.value = {
    title: String(title),
    description,
    x,
    y,
  };
};

const hideTooltip = () => {
  activeTooltip.value = null;
};
</script>

<style scoped>
.kanban-board {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
}

.board-layout {
  display: flex;
  gap: 1rem;
  height: 100%;
  overflow: hidden;
  flex: 1;
}

.competency-groups-section {
  width: 300px;
  min-width: 300px;
  border-radius: 0.5rem;
}

.competency-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.scroll-competency-section {
  overflow-y: scroll;
  height: 92%;
}

.competency-group {
  background: #ebecf0;
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  text-align: left;
  cursor: grab;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.competency-group:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.competency-group.dragging {
  opacity: 0.5;
  background: #e3f2fd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.group-header-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  cursor: grab;
}

.group-header.group-disabled {
  cursor: pointer;
  opacity: 0.6;
  background-color: #f8f9fa;
}

.group-header.group-disabled .drag-handle {
  opacity: 0.3;
  cursor: not-allowed;
}

.group-title-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.group-title {
  font-weight: 600;
  color: #172b4d;
}

.drag-handle {
  color: #5e6c84;
  font-size: 1rem;
  margin-right: 0.5rem;
  opacity: 0.6;
  cursor: grab;
}

.expand-icon {
  color: #5e6c84;
  font-size: 1.5rem;
}

.group-competencies {
  padding: 0.5rem;
  background: white;
}

.competency-item {
  padding: 0.75rem;
  background: white;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  cursor: grab;
}

.competency-item[data-type="Technical"] {
  background: var(--primary-color);
  color: white;
  border: 2px solid #40916c;
}

.competency-item[data-type="Technical"]:hover {
  background: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.competency-item[data-type="Technical"] .info-button {
  color: white;
}

.competency-item[data-type="Technical"] .info-button:hover {
  color: #d8f3dc;
  background-color: rgba(255, 255, 255, 0.1);
}

.competency-item[data-type="Non Technical"] {
  background: var(--secondary-color);
  color: white;
  border: 2px solid var(--secondary-color);
}

.competency-item[data-type="Non Technical"]:hover {
  background: var(--secondary-color);
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.competency-item[data-type="Non Technical"] .info-button {
  color: white;
}

.competency-item[data-type="Non Technical"] .info-button:hover {
  color: #e2e8f0;
  background-color: rgba(255, 255, 255, 0.1);
}

.competency-item:active {
  transform: scale(0.98);
}

.competency-item.dragging {
  opacity: 0.5;
  background: #e3f2fd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.proficiencies-section {
  flex: 1;
  overflow: auto;
  border-radius: 0.5rem;
  position: relative;
  min-height: 0;
}

.proficiencies-grid {
  display: flex;
  gap: 1rem;
  height: 100%;
  min-width: min-content;
  overflow-x: scroll;
  overflow-y: visible;
  padding-bottom: 1rem;
  position: sticky;
  top: 0;
}

.proficiency-column {
  width: 300px;
  min-width: 300px;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
}

.proficiency-box {
  background: #ebecf0;
  border-radius: 0.375rem;
  border: none;
  transition: all 0.2s ease;
  height: 100%;
  width: 300px;
  min-width: 300px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin: 0;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.proficiency-box:hover {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.proficiency-box.drag-over {
  background: #e3f2fd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.proficiency-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  border-radius: 0.375rem 0.375rem 0 0;
  border: 4px solid #e2e8f0;
  border-bottom: 2px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
}

.proficiency-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #172b4d;
}

.dropped-competencies {
  padding: 0;
  background: #ebecf0;
  border-radius: 0 0 0.375rem 0.375rem;
  overflow-y: scroll;
  flex: 1;
  min-height: 0;
  text-align: left;
  border: 4px solid #e2e8f0;
  border-top: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.competencies-list {
  height: 100%;
  padding: 0.5rem;
}

.dropped-group {
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  overflow: hidden;
  background: white;
  cursor: grab;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.dropped-group:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dropped-group.dragging {
  opacity: 0.5;
  background: #e3f2fd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.group-header-dropped {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  color: #172b4d;
}

.group-title-container-dropped {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.drag-handle-dropped {
  color: #5e6c84;
  font-size: 1rem;
  opacity: 0.6;
  cursor: grab;
}

.group-title-dropped {
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-group-button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #5e6c84;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: auto;
}

.remove-group-button:hover {
  color: #eb5a46;
  background-color: rgba(235, 90, 70, 0.1);
}

.group-competencies-dropped {
  padding: 0.5rem;
}

.dropped-item {
  padding: 0.75rem;
  background: white;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  position: relative;
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.dropped-item[data-type="Technical"] {
  background: var(--primary-color);
  color: white;
  border: 2px solid var(--primary-color);
}

.dropped-item[data-type="Technical"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dropped-item[data-type="Technical"] .group-name {
  color: #d8f3dc;
}

.dropped-item[data-type="Technical"] .info-button {
  color: white;
}

.dropped-item[data-type="Technical"] .info-button:hover {
  color: #d8f3dc;
  background-color: rgba(255, 255, 255, 0.1);
}

.dropped-item[data-type="Technical"] .remove-button {
  color: #d8f3dc;
}

.dropped-item[data-type="Technical"] .remove-button:hover {
  background-color: rgba(255, 107, 107, 0.1);
}

.dropped-item[data-type="Non Technical"] {
  background: var(--secondary-color);
  color: white;
  border: 2px solid var(--secondary-color);
}

.dropped-item[data-type="Non Technical"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dropped-item[data-type="Non Technical"] .group-name {
  color: #e2e8f0;
}

.dropped-item[data-type="Non Technical"] .info-button {
  color: white;
}

.dropped-item[data-type="Non Technical"] .info-button:hover {
  color: #e2e8f0;
  background-color: rgba(255, 255, 255, 0.1);
}

.dropped-item[data-type="Non Technical"] .remove-button {
  color: #e2e8f0;
}

.dropped-item[data-type="Non Technical"] .remove-button:hover {
  background-color: rgba(255, 107, 107, 0.1);
}

.dropped-item:active {
  transform: scale(0.98);
}

.dropped-item::after {
  content: "⋮⋮";
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.5;
  font-size: 1rem;
  letter-spacing: 1px;
  color: #5e6c84;
}

.dropped-item.dragging {
  opacity: 0.5;
  background: #e3f2fd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.group-name {
  color: #5e6c84;
  font-size: 0.75rem;
  font-style: italic;
}

.remove-button {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #dae2e9;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.remove-button:hover {
  color: #eb5a46;
  background-color: rgba(235, 90, 70, 0.1);
}

.remove-button:active {
  transform: translateY(-50%) scale(0.95);
}

.tooltip-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  z-index: 9999;
}

.info-button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #5e6c84;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.info-button:hover {
  color: #172b4d;
  background-color: rgba(0, 0, 0, 0.05);
}

.tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 10000;
  background-color: #172b4d;
  color: white;
  text-align: left;
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 300px;
  opacity: 0;
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  white-space: normal;
  word-wrap: break-word;
}

.tooltip-container:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.tooltip::after {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 20px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #172b4d transparent;
}

.global-tooltip {
  position: fixed;
  z-index: 10000;
  background-color: #172b4d;
  color: white;
  text-align: left;
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 300px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  white-space: normal;
  word-wrap: break-word;
  pointer-events: none;
}

.tooltip-title {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: #fff;
}

.tooltip-description {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
}

.global-tooltip::after {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 20px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #172b4d transparent;
}

.competency-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.competency-title {
  flex: 1;
}

.proficiencies-section.summary-mode {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem;
}

.proficiencies-grid.summary-mode {
  justify-content: center;
  gap: 2rem;
}

.proficiency-box.summary-mode {
  height: auto;
  min-height: fit-content;
  max-height: none;
}

.proficiency-box.summary-mode .dropped-competencies {
  max-height: none;
  height: auto;
}

.proficiency-box.summary-mode .competencies-list {
  height: auto;
  max-height: none;
}

.filter-checkboxes {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: white;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  user-select: none;
}

.checkbox-label:first-child {
  background-color: var(--primary-color);
  border: 2px solid var(--primary-color);
}

.checkbox-label:first-child:hover {
  transform: translateY(-1px);
}

.checkbox-label:last-child {
  background-color: var(--secondary-color);
  border: 2px solid var(--secondary-color);
}

.checkbox-label:last-child:hover {
  transform: translateY(-1px);
}

.checkbox-label input[type="checkbox"] {
  display: none;
}

.checkbox-label input[type="checkbox"]:checked + span {
  color: white;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"]:checked + span::before {
  content: "";
  margin-right: 0.5rem;
  color: white;
  display: inline-block;
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'%3E%3C/path%3E%3Ccircle cx='12' cy='12' r='3'%3E%3C/circle%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}

.checkbox-label input[type="checkbox"]:not(:checked) + span::before {
  content: "";
  margin-right: 0.5rem;
  color: white;
  display: inline-block;
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24'%3E%3C/path%3E%3Cline x1='1' y1='1' x2='23' y2='23'%3E%3C/line%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}

.checkbox-label span {
  display: flex;
  align-items: center;
}

.type-indicators {
  display: flex;
  gap: 0.25rem;
}

.type-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.type-dot.technical {
  background-color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.type-dot.non-technical {
  background-color: var(--secondary-color);
  border: 1px solid var(--secondary-color);
}

/* Mobile responsive styles */
@media (max-width: 1024px) {
  .board-layout {
    flex-direction: column;
    gap: 0.5rem;
  }

  .competency-groups-section {
    width: 100%;
    min-width: auto;
    max-height: 40vh;
    overflow-y: auto;
  }

  .scroll-competency-section {
    height: auto;
    max-height: calc(40vh - 3rem);
  }

  .proficiencies-section {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }
}

@media (max-width: 768px) {
  .competency-groups-section {
    max-height: 35vh;
  }

  .scroll-competency-section {
    max-height: calc(35vh - 3rem);
  }

  .proficiency-column {
    width: 250px;
    min-width: 250px;
  }

  .proficiency-box {
    width: 250px;
    min-width: 250px;
  }
}

@media (max-width: 480px) {
  .competency-groups-section {
    max-height: 30vh;
  }

  .scroll-competency-section {
    max-height: calc(30vh - 3rem);
  }

  .proficiency-column {
    width: 220px;
    min-width: 220px;
  }

  .proficiency-box {
    width: 220px;
    min-width: 220px;
  }

  .filter-checkboxes {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .checkbox-label {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
}
</style>
